import { useEffect, useState } from 'react';

export const DRAWER_WIDTH = 220;

const DESKTOP_QUERY = '(min-width:1024px)';

type SidebarListener = () => void;

interface SidebarState {
    isDesktop: boolean;
    mobileOpen: boolean;
    desktopCollapsed: boolean;
}

const readIsDesktop = (): boolean => (
    typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
);

let state: SidebarState = {
    isDesktop: readIsDesktop(),
    mobileOpen: false,
    desktopCollapsed: false
};

const listeners = new Set<SidebarListener>();

const isDrawerOpen = (sidebarState: SidebarState): boolean => (
    sidebarState.isDesktop ? !sidebarState.desktopCollapsed : sidebarState.mobileOpen
);

const notify = (): void => {
    listeners.forEach((listener) => listener());
};

export const getSidebarSnapshot = () => ({
    isDesktop: state.isDesktop,
    isDrawerOpen: isDrawerOpen(state)
});

export const subscribeSidebar = (listener: SidebarListener): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const toggleDrawer = (): void => {
    if (state.isDesktop) {
        state = { ...state, desktopCollapsed: !state.desktopCollapsed };
    } else {
        state = { ...state, mobileOpen: !state.mobileOpen };
    }
    notify();
};

export const closeDrawer = (): void => {
    if (!state.isDesktop) {
        state = { ...state, mobileOpen: false };
        notify();
    }
};

if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const onViewportChange = (): void => {
        state = {
            ...state,
            isDesktop: mediaQuery.matches,
            mobileOpen: false
        };
        notify();
    };
    mediaQuery.addEventListener('change', onViewportChange);
}

export const useSidebar = () => {
    const [snapshot, setSnapshot] = useState(getSidebarSnapshot);

    useEffect(() => subscribeSidebar(() => {
        setSnapshot(getSidebarSnapshot());
    }), []);

    return {
        isDesktop: snapshot.isDesktop,
        isDrawerOpen: snapshot.isDrawerOpen,
        toggleDrawer,
        closeDrawer
    };
};
